import { recordRecentFrame } from "./recent-opened";
import {
  addFrameToFocusPresentation,
  presentFrameAt,
} from "@/lib/presentation-mode";
import { expandConversationCompanionStackFrame } from "@/lib/conversation-companion-focus-layout";
import { presentArtifactBesideConversation } from "@/lib/conversation-artifact-presentation";
import { replaceFocusPresentationMode } from "@/lib/focus-presentation";
import { makeFocusFrameMain } from "@/lib/focus-window-presentation";
import { conversationFrameTaskId } from "@/components/shapes/conversation-frame/conversation-frame-utils";
import { useCanvasStore } from "@/stores/canvas-store";
import { useFramesStore } from "@/stores/frames-store";
import { usePresentationStore } from "@/stores/presentation-store";
import {
  isFrameHiddenByMetadata,
  selectFramesByType,
} from "@/stores/frames-selectors";
import { setFrameHidden } from "@/lib/frame-visibility";
import { openFramesNow } from "@/lib/open-frames";
import type { FocusCardOrigin } from "@/lib/focus/focus-types";
import { withUserKept } from "@/lib/focus/focus-plan-rounds";

/** Where an open started: a wall card opens beside its task's chat (Q4). */
export type OpenFocusOrigin = "card" | "navigate" | "comment";

/**
 * Bring a focus window to the front without reapplying the managed focus
 * layout: click and drag use this path, so activating a window never moves
 * it, nor makes a side frame the main one.
 */
export function selectFrameWindow(frameId: string): boolean {
  if (!useFramesStore.getState().framesById[frameId]) return false;
  if (expandConversationCompanionStackFrame(frameId)) return true;

  const { presentation, setPresentationActiveIndex } =
    usePresentationStore.getState();
  if (presentation?.variant === "focus") {
    const focusIndex = presentation.frameIds.indexOf(frameId);
    if (focusIndex >= 0) setPresentationActiveIndex(focusIndex);
  }
  useCanvasStore.getState().selectFrame(frameId);
  return true;
}

/** In the open focus: add a new frame or make a shown one the main frame
 *  (an open is deliberate); a chat's layout presents it instead. */
function showInOpenFocus(frameId: string): boolean {
  const frame = useFramesStore.getState().framesById[frameId];
  if (!frame) return false;
  if (isFrameHiddenByMetadata(frame)) setFrameHidden(frameId, false);

  const presentation = usePresentationStore.getState().presentation;
  if (presentation?.variant !== "focus") return false;
  const index = presentation.frameIds.indexOf(frameId);
  if (index < 0) addFrameToFocusPresentation(frameId);
  else if (!makeFocusFrameMain(frameId)) {
    if (index === presentation.activeIndex) {
      useCanvasStore.getState().selectFrame(frameId);
    } else presentFrameAt(index);
  }
  // The user opened it: a new agent round keeps it (plan 13).
  const opened = usePresentationStore.getState().presentation;
  const kept = opened && withUserKept(opened, frameId);
  if (kept && kept !== opened) {
    usePresentationStore.setState({ presentation: kept });
  }
  return true;
}

/** A loaded chat working on the frame's task, if any. */
function chatForTask(frameId: string): string | null {
  const frames = useFramesStore.getState();
  const taskId = frames.framesById[frameId]?.taskTicket?.id;
  if (!taskId) return null;
  const chat = selectFramesByType(frames, "conversation").find(
    (candidate) => conversationFrameTaskId(candidate) === taskId,
  );
  return chat?.frameId ?? null;
}

/**
 * Show frames with today's activation rules: while focus is open each one
 * is selected, presented or added in place; otherwise focus starts with
 * exactly these frames, and a card made for a task opens beside that task's
 * loaded chat. A card opens as a preview above the wall; a link,
 * notification or comment enters focus mode (`FocusPlacement`). It calls
 * focus commands only, never the navigation it is reached from, so no open
 * path loops. A card's preview grows from `fromCard`; one beside its chat
 * keeps that layout's entrance.
 */
export function openInFocus(
  frameIds: readonly string[],
  {
    activeId,
    origin,
    fromCard,
  }: {
    activeId?: string;
    origin: OpenFocusOrigin;
    fromCard?: FocusCardOrigin;
  },
): boolean {
  const { framesById } = useFramesStore.getState();
  const ids = [...new Set(frameIds)].filter((id) => !!framesById[id]);
  const active = activeId && ids.includes(activeId) ? activeId : ids[0];
  if (!active) return false;
  if (usePresentationStore.getState().presentation?.variant === "focus") {
    for (const id of ids) if (id !== active) showInOpenFocus(id);
    return showInOpenFocus(active);
  }
  const placement = origin === "card" ? "preview" : "focus";
  const chatId =
    origin === "card" && ids.length === 1 ? chatForTask(active) : null;
  if (chatId) {
    return presentArtifactBesideConversation(chatId, active, { placement });
  }
  replaceFocusPresentationMode(active, ids, {
    placement,
    fromCard: origin === "card" ? fromCard : undefined,
  });
  return true;
}

/** Show a frame in the current presentation; a successful open is remembered as Recent. */
export function activateFrameInCurrentContext(frameId: string): boolean {
  const inFocus =
    usePresentationStore.getState().presentation?.variant === "focus";
  // Below the open-focus branch, so open focus adds in place (decision 15);
  // the wall's hand-off remembers its own opens.
  if (!inFocus && openFramesNow([frameId], "navigate")) return true;
  const activated = inFocus
    ? showInOpenFocus(frameId)
    : useCanvasStore.getState().navigateToFrame(frameId);
  if (activated) recordRecentFrame(frameId);
  return activated;
}
